import { IUserProfile } from "../../interfaces/user.interface";
import { NavLink } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getEndUserData } from "services/setting.service";
import { getPageTitle } from "utills/page-title";
import { logout } from "services/auth.service";
import { useLocation } from "react-router-dom";

function PrivateHeader({ userInfo }: IUserProfile) {
  const [toggleSidebar, setToggleSidebar] = useState(false);
  //requires useLocation() to access the current URL pathname smoothly i.e. pageTitle
  const location = useLocation();

  const logoutUser = useMutation({
    mutationFn: () => logout(),
    onSuccess: async () => {
      await localStorage.removeItem("userInfo");
      window.location.reload();
    },
  });
  const handleSubmit = async (ev: React.MouseEvent<HTMLDivElement>) => {
    ev.preventDefault();
    // logoutUser.mutateAsync()
    await localStorage.removeItem("userInfo");
    window.location.reload();
  };
  const handleSidebarToggle = () => {
    setToggleSidebar(!toggleSidebar);
  };
  const { data: endUserInfo } = useQuery({
    queryKey: ["getEndUserData"],
    queryFn: getEndUserData,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const sidebarRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      sidebarRef.current &&
      !sidebarRef.current.contains(event.target as Node)
    ) {
      setToggleSidebar(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <nav className="sticky left-0 top-0 z-40 w-full bg-white border-gray-200 sm:pl-64">
        <div className="px-3 py-3 shadow-md lg:px-5 lg:pl-3 h-[86px] flex items-center justify-between">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center justify-start rtl:justify-end">
              <button
                data-drawer-target="logo-sidebar"
                data-drawer-toggle="logo-sidebar"
                aria-controls="logo-sidebar"
                type="button"
                className="inline-flex items-center p-2 text-sm text-gray-500 rounded-lg sm:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600"
                onClick={handleSidebarToggle}
              >
                <span className="sr-only">Open sidebar</span>
                <svg
                  className="w-6 h-6"
                  aria-hidden="true"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    clipRule="evenodd"
                    fillRule="evenodd"
                    d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"
                  ></path>
                </svg>
              </button>
              <div className="flex items-center text-2xl font-bold capitalize text-headerBlue sm:ml-5">
                {getPageTitle(window.location.pathname, endUserInfo?.role)}
              </div>
            </div>

            <div className="flex items-center ">
              <div className="flex items-center ms-3 min-h-10">
                <div className="md:hidden">
                  <button
                    hidden
                    type="button"
                    className="flex text-sm bg-gray-800 rounded-full focus:ring-4 focus:ring-gray-300 dark:focus:ring-gray-600"
                    aria-expanded="false"
                    data-dropdown-toggle="dropdown-user"
                  >
                    <span className="sr-only">Open user menu</span>
                  </button>
                </div>
                <div
                  className={`z-50 md:hidden ${
                    toggleSidebar ? "block" : "hidden"
                  } my-4 text-base list-none bg-white divide-y divide-gray-100 rounded shadow dark:bg-gray-700 dark:divide-gray-600 ${
                    toggleSidebar ? "absolute left-0" : "fixed"
                  }`}
                  id="dropdown-user"
                >
                  <div className="px-4 py-3" role="none">
                    <p
                      className="text-sm text-gray-900 dark:text-white"
                      role="none"
                    >
                      {userInfo.full_name}
                    </p>
                    <p
                      className="text-sm font-medium text-gray-900 truncate dark:text-gray-300"
                      role="none"
                    >
                      {userInfo.email}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <aside
        id="logo-sidebar"
        ref={sidebarRef}
        className={`${
          toggleSidebar ? "translate-x-0" : "-translate-x-full"
        } fixed top-0 left-0 z-50 w-64 h-screen transition-transform bg-lightBlue border-lightBlue-200 sm:translate-x-0`}
        aria-label="Sidebar"
      >
        <div className="h-full px-3 pb-4 bg-lightBlue">
          {/* <div className="flex flex-col justify-between h-[calc(100vh-50px)] pb-5"> */}
          <div className="flex flex-col justify-between h-[calc(100vh-50px)] pb-5 sm:h-screen">
            <div>
              <div className="flex items-center justify-between h-20">
                <a href="/" className="flex">
                  <img
                    src="/assets/images/GlimpseLogo_White.svg"
                    className="min-h-[35px] w-[135px] me-3"
                    alt="Glimpse Logo"
                  />
                </a>
                <span onClick={handleSidebarToggle} className="flex sm:hidden">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M0.344118 19.6559C-0.114706 19.1971 -0.114706 18.4529 0.344118 17.9912L17.9912 0.344118C18.45 -0.114706 19.1971 -0.114706 19.6559 0.344118C20.1147 0.802941 20.1147 1.55 19.6559 2.00882L2.00882 19.6559C1.55 20.1147 0.805882 20.1147 0.344118 19.6559Z"
                      fill="#60849F"
                    />
                    <path
                      d="M17.9912 19.6559L0.344118 2.00882C-0.114706 1.55 -0.114706 0.802941 0.344118 0.344118C0.802941 -0.114706 1.55 -0.114706 2.00882 0.344118L19.6559 17.9912C20.1147 18.45 20.1147 19.1941 19.6559 19.6559C19.1971 20.1147 18.45 20.1147 17.9912 19.6559Z"
                      fill="#60849F"
                    />
                  </svg>
                </span>
              </div>
              <div className="pt-5 space-y-2 font-medium">
                {userInfo.role === "super" && (
                  <>
                    <NavLink
                      onClick={() => setToggleSidebar(!toggleSidebar)}
                      to="/admin-dashboard"
                      className={({ isActive, isPending }) =>
                        [
                          isPending ? "pending" : "",
                          isActive ? "rounded-lg bg-headerBlue" : "",
                        ].join("menu ")
                      }
                    >
                      {/* <div className="flex items-center"> */}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        className="w-6 h-6"
                      >
                        <path
                          d="M6.62534 1H2.12534C1.50402 1 1.00034 1.50368 1.00034 2.125V6.625C1.00034 7.24632 1.50402 7.75 2.12534 7.75H6.62534C7.24666 7.75 7.75034 7.24632 7.75034 6.625V2.125C7.75034 1.50368 7.24666 1 6.62534 1Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M17.8749 1H13.3749C12.7536 1 12.2499 1.50368 12.2499 2.125V6.625C12.2499 7.24632 12.7536 7.75 13.3749 7.75H17.8749C18.4962 7.75 18.9999 7.24632 18.9999 6.625V2.125C18.9999 1.50368 18.4962 1 17.8749 1Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6.625 12.2502H2.125C1.50368 12.2502 1 12.7539 1 13.3752V17.8752C1 18.4966 1.50368 19.0002 2.125 19.0002H6.625C7.24632 19.0002 7.75 18.4966 7.75 17.8752V13.3752C7.75 12.7539 7.24632 12.2502 6.625 12.2502Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M17.8749 12.2502H13.3749C12.7536 12.2502 12.2499 12.7539 12.2499 13.3752V17.8752C12.2499 18.4966 12.7536 19.0002 13.3749 19.0002H17.8749C18.4962 19.0002 18.9999 18.4966 18.9999 17.8752V13.3752C18.9999 12.7539 18.4962 12.2502 17.8749 12.2502Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span className="ms-3">Overview</span>
                      {/* </div> */}
                    </NavLink>
                    <NavLink
                      onClick={() => setToggleSidebar(!toggleSidebar)}
                      to="/partners"
                      className={({ isActive, isPending }) =>
                        [
                          isPending ? "pending" : "",
                          isActive ? "rounded-lg bg-headerBlue" : "",
                        ].join("menu ")
                      }
                    >
                      <svg
                        width={20}
                        height={20}
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_4641_43902)">
                          <path
                            d="M10 11.6667C9.91667 11.6667 9.83333 11.6667 9.75 11.5834L0.583333 8.25002C0.25 8.16669 0 7.83335 0 7.50002C0 7.16669 0.25 6.83335 0.583333 6.75002L9.75 3.41669C9.91667 3.33335 10.1667 3.33335 10.3333 3.41669L19.5 6.75002C19.75 6.83335 20 7.16669 20 7.50002C20 7.83335 19.75 8.16669 19.4167 8.25002L10.25 11.5834C10.1667 11.6667 10.0833 11.6667 10 11.6667ZM3.25 7.50002L10 9.91669L16.75 7.50002L10 5.08335L3.25 7.50002Z"
                            fill="white"
                            stroke="white"
                            strokeWidth="0.5"
                          />
                          <path
                            d="M15.8333 16.6667H10C9.5 16.6667 9.16666 16.3333 9.16666 15.8333C9.16666 15.3333 9.5 15 10 15H15C14.9167 12.8333 14.4167 11 13.5833 9.66668C13.3333 9.25001 13.5 8.75001 13.8333 8.50001C14.25 8.25001 14.75 8.41668 15 8.75001C16.0833 10.5 16.6667 12.9167 16.6667 15.75C16.6667 16.3333 16.3333 16.6667 15.8333 16.6667Z"
                            fill="white"
                            stroke="white"
                            strokeWidth="0.5"
                          />
                          <path
                            d="M10 16.6667H4.16667C3.66667 16.6667 3.33334 16.3334 3.33334 15.8334C3.33334 13 3.91667 10.5834 5 8.83335C5.25 8.41669 5.75 8.33335 6.16667 8.58335C6.58334 8.83335 6.66667 9.33335 6.41667 9.75002C5.58334 11.0834 5.08334 12.9167 5 15.0834H10C10.5 15.0834 10.8333 15.4167 10.8333 15.9167C10.8333 16.4167 10.5 16.6667 10 16.6667ZM18.3333 16.6667C17.8333 16.6667 17.5 16.3334 17.5 15.8334V7.50002C17.5 7.00002 17.8333 6.66669 18.3333 6.66669C18.8333 6.66669 19.1667 7.00002 19.1667 7.50002V15.8334C19.1667 16.3334 18.8333 16.6667 18.3333 16.6667Z"
                            fill="white"
                            stroke="white"
                            strokeWidth="0.5"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_4641_43902">
                            <rect width={20} height={20} fill="white" />
                          </clipPath>
                        </defs>
                      </svg>

                      <span className="ms-3">Partners</span>
                    </NavLink>
                    <NavLink
                      onClick={() => setToggleSidebar(!toggleSidebar)}
                      to="/individuals"
                      className={({ isActive, isPending }) =>
                        [
                          isPending ? "pending" : "",
                          isActive ? "rounded-lg bg-headerBlue" : "",
                        ].join("menu ")
                      }
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="20"
                        viewBox="0 0 16 20"
                        fill="none"
                        className="w-6 h-6"
                      >
                        <path
                          d="M7.75038 7.75C9.61434 7.75 11.1254 6.23896 11.1254 4.375C11.1254 2.51104 9.61434 1 7.75038 1C5.88642 1 4.37538 2.51104 4.37538 4.375C4.37538 6.23896 5.88642 7.75 7.75038 7.75Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5.50011 11.125H10.0001C12.4835 11.125 14.5001 13.1416 14.5001 15.625V16.75C14.5001 17.9931 13.4932 19 12.2501 19H3.25011C2.00698 19 1.00011 17.9931 1.00011 16.75V15.625C1.00011 13.1416 3.01667 11.125 5.50011 11.125Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span className="ms-3">Individuals</span>
                    </NavLink>
                    <NavLink
                      onClick={() => setToggleSidebar(!toggleSidebar)}
                      to="/careers"
                      className={({ isActive, isPending }) =>
                        [
                          isPending ? "pending" : "",
                          isActive ? "rounded-lg bg-headerBlue" : "",
                        ].join("menu ")
                      }
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-6 h-6"
                      >
                        <path
                          d="M19.6669 6.49994H3.33357C2.04491 6.49994 1.00024 7.54461 1.00024 8.83327V19.3333C1.00024 20.6219 2.04491 21.6666 3.33357 21.6666H19.6669C20.9556 21.6666 22.0002 20.6219 22.0002 19.3333V8.83327C22.0002 7.54461 20.9556 6.49994 19.6669 6.49994Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8.00034 6.5V5.33333C8.00034 4.04417 9.0445 3 10.3337 3H12.667C13.9562 3 15.0003 4.04417 15.0003 5.33333V6.5"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M1.00024 11.1667C1.00024 13.1005 2.56649 14.6667 4.50024 14.6667H18.5002C20.434 14.6667 22.0002 13.1005 22.0002 11.1667"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>

                      <span className="ms-3">Careers</span>
                    </NavLink>
                  </>
                )}
                {userInfo.role === "user" && (
                  <>
                    <NavLink
                      onClick={() => setToggleSidebar(!toggleSidebar)}
                      to="/user-careers?tab=preview"
                      className={({ isActive, isPending }) =>
                        [
                          isPending ? "pending" : "",
                          isActive ? "rounded-lg bg-headerBlue" : "",
                        ].join("menu ")
                      }
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-6 h-6"
                      >
                        <path
                          d="M19.6669 6.49994H3.33357C2.04491 6.49994 1.00024 7.54461 1.00024 8.83327V19.3333C1.00024 20.6219 2.04491 21.6666 3.33357 21.6666H19.6669C20.9556 21.6666 22.0002 20.6219 22.0002 19.3333V8.83327C22.0002 7.54461 20.9556 6.49994 19.6669 6.49994Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8.00034 6.5V5.33333C8.00034 4.04417 9.0445 3 10.3337 3H12.667C13.9562 3 15.0003 4.04417 15.0003 5.33333V6.5"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M1.00024 11.1667C1.00024 13.1005 2.56649 14.6667 4.50024 14.6667H18.5002C20.434 14.6667 22.0002 13.1005 22.0002 11.1667"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>

                      <span className="ms-3">Careers</span>
                    </NavLink>
                    <NavLink
                      onClick={() => setToggleSidebar(!toggleSidebar)}
                      to="/prioritize"
                      className={({ isActive, isPending }) =>
                        [
                          isPending ? "pending" : "",
                          isActive ? "rounded-lg bg-headerBlue" : "",
                        ].join("menu ")
                      }
                    >
                      <svg
                        width={20}
                        height={18}
                        viewBox="0 0 20 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M17 1H2.99997C1.8954 1 0.999969 1.89543 0.999969 3V5C0.999969 6.10457 1.8954 7 2.99997 7H17C18.1045 7 19 6.10457 19 5V3C19 1.89543 18.1045 1 17 1Z"
                          stroke="white"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M16.9998 11H2.99976C1.89519 11 0.999756 11.8954 0.999756 13V15C0.999756 16.1046 1.89519 17 2.99976 17H16.9998C18.1043 17 18.9998 16.1046 18.9998 15V13C18.9998 11.8954 18.1043 11 16.9998 11Z"
                          stroke="white"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>

                      <span className="ms-3">Prioritize</span>
                    </NavLink>
                    <NavLink
                      onClick={() => setToggleSidebar(!toggleSidebar)}
                      to="/career-awareness"
                      className={({ isActive, isPending }) =>
                        [
                          isPending ? "pending" : "",
                          isActive ? "rounded-lg bg-headerBlue" : "",
                        ].join("menu ")
                      }
                    >
                      <svg
                        width="22"
                        height="22"
                        viewBox="0 0 22 22"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11.6981 6.48081L12.5123 8.54932H15.2316C15.9114 8.54932 16.2552 9.36954 15.7786 9.85543L14.0298 11.6334L14.98 15.0142C15.1738 15.7048 14.4174 16.2656 13.8298 15.8672L11.4199 14.2331C11.1636 14.0596 10.8277 14.0596 10.5729 14.2331L8.16311 15.8672C7.57551 16.2656 6.8191 15.7032 7.01288 15.0142L7.96307 11.6334L6.22367 9.8648C5.74233 9.37579 6.08927 8.54776 6.77534 8.54776H9.48054L10.2947 6.47924C10.5464 5.84025 11.4496 5.84025 11.7013 6.47924L11.6981 6.48081Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M11 21C16.5228 21 21 16.5228 21 11C21 5.47715 16.5228 1 11 1C5.47715 1 1 5.47715 1 11C1 16.5228 5.47715 21 11 21Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>

                      <span className="ms-3">My Career Awareness</span>
                    </NavLink>
                  </>
                )}
                {userInfo.role === "partner" && (
                  <>
                    <NavLink
                      onClick={() => setToggleSidebar(!toggleSidebar)}
                      to="/partner-dashboard"
                      className={({ isActive, isPending }) =>
                        [
                          isPending ? "pending" : "",
                          isActive ? "rounded-lg bg-headerBlue" : "",
                        ].join("menu ")
                      }
                    >
                      <svg
                        width={24}
                        height={24}
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8.62534 3H4.12534C3.50402 3 3.00034 3.50368 3.00034 4.125V8.625C3.00034 9.24632 3.50402 9.75 4.12534 9.75H8.62534C9.24666 9.75 9.75034 9.24632 9.75034 8.625V4.125C9.75034 3.50368 9.24666 3 8.62534 3Z"
                          stroke="white"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M19.8749 3H15.3749C14.7536 3 14.2499 3.50368 14.2499 4.125V8.625C14.2499 9.24632 14.7536 9.75 15.3749 9.75H19.8749C20.4963 9.75 20.9999 9.24632 20.9999 8.625V4.125C20.9999 3.50368 20.4963 3 19.8749 3Z"
                          stroke="white"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8.625 14.2502H4.125C3.50368 14.2502 3 14.7539 3 15.3752V19.8752C3 20.4966 3.50368 21.0002 4.125 21.0002H8.625C9.24632 21.0002 9.75 20.4966 9.75 19.8752V15.3752C9.75 14.7539 9.24632 14.2502 8.625 14.2502Z"
                          stroke="white"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M19.8749 14.2502H15.3749C14.7536 14.2502 14.2499 14.7539 14.2499 15.3752V19.8752C14.2499 20.4966 14.7536 21.0002 15.3749 21.0002H19.8749C20.4963 21.0002 20.9999 20.4966 20.9999 19.8752V15.3752C20.9999 14.7539 20.4963 14.2502 19.8749 14.2502Z"
                          stroke="white"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>

                      <span className="ms-3">Dashboard</span>
                    </NavLink>
                    <NavLink
                      onClick={() => setToggleSidebar(!toggleSidebar)}
                      to="/partner-careers"
                      className={({ isActive, isPending }) =>
                        [
                          isPending ? "pending" : "",
                          isActive ? "rounded-lg bg-headerBlue" : "",
                        ].join("menu ")
                      }
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-6 h-6"
                      >
                        <path
                          d="M19.6669 6.49994H3.33357C2.04491 6.49994 1.00024 7.54461 1.00024 8.83327V19.3333C1.00024 20.6219 2.04491 21.6666 3.33357 21.6666H19.6669C20.9556 21.6666 22.0002 20.6219 22.0002 19.3333V8.83327C22.0002 7.54461 20.9556 6.49994 19.6669 6.49994Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8.00034 6.5V5.33333C8.00034 4.04417 9.0445 3 10.3337 3H12.667C13.9562 3 15.0003 4.04417 15.0003 5.33333V6.5"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M1.00024 11.1667C1.00024 13.1005 2.56649 14.6667 4.50024 14.6667H18.5002C20.434 14.6667 22.0002 13.1005 22.0002 11.1667"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>

                      <span className="ms-3">Careers</span>
                    </NavLink>
                    <NavLink
                      onClick={() => setToggleSidebar(!toggleSidebar)}
                      to="/coaches-corner"
                      className={({ isActive, isPending }) =>
                        [
                          isPending ? "pending" : "",
                          isActive ? "rounded-lg bg-headerBlue" : "",
                        ].join("menu ")
                      }
                    >
                      <svg
                        width={24}
                        height={24}
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M2.43375 10.824C1.8405 11.8282 1.5 12.9998 1.5 14.25C1.5 17.9753 4.52475 21 8.25 21C11.7218 21 14.5852 18.3728 14.9587 15H21C21.378 15 21.6975 14.718 21.744 14.343L22.494 8.343C22.521 8.12925 22.455 7.91475 22.3125 7.7535C22.17 7.59225 21.9653 7.5 21.75 7.5H15C14.7157 7.5 14.4563 7.6605 14.3295 7.91475L13.7865 9H11.7135L11.1705 7.91475C11.0437 7.6605 10.7843 7.5 10.5 7.5H8.25C7.36875 7.5 6.5265 7.6695 5.754 7.977C5.38125 7.24875 4.62375 6.75 3.75 6.75C2.508 6.75 1.5 7.758 1.5 9C1.5 9.75075 1.86825 10.4153 2.43375 10.824ZM8.25 9H10.0365L10.5795 10.0852C10.7063 10.3395 10.9657 10.5 11.25 10.5H14.25C14.5343 10.5 14.7937 10.3395 14.9205 10.0852L15.4635 9H20.9002L20.3377 13.5H14.25C13.836 13.5 13.5 13.836 13.5 14.25C13.5 17.1473 11.1473 19.5 8.25 19.5C5.35275 19.5 3 17.1473 3 14.25C3 11.3527 5.35275 9 8.25 9ZM8.25 10.5C6.18 10.5 4.5 12.18 4.5 14.25C4.5 16.32 6.18 18 8.25 18C10.32 18 12 16.32 12 14.25C12 12.18 10.32 10.5 8.25 10.5ZM8.25 12C9.492 12 10.5 13.008 10.5 14.25C10.5 15.492 9.492 16.5 8.25 16.5C7.008 16.5 6 15.492 6 14.25C6 13.008 7.008 12 8.25 12ZM4.431 8.685C4.31175 8.4285 4.0515 8.25 3.75 8.25C3.336 8.25 3 8.586 3 9C3 9.261 3.1335 9.4905 3.33525 9.62475C3.66525 9.2745 4.03275 8.95875 4.431 8.685ZM14.871 4.31775L13.8105 5.379C13.518 5.6715 13.518 6.147 13.8105 6.4395C14.103 6.732 14.5785 6.732 14.871 6.4395L15.9323 5.379C16.2247 5.08575 16.2247 4.611 15.9323 4.31775C15.639 4.02525 15.1642 4.02525 14.871 4.31775ZM9.56775 5.379L10.629 6.4395C10.9215 6.732 11.397 6.732 11.6895 6.4395C11.982 6.147 11.982 5.6715 11.6895 5.379L10.629 4.31775C10.3357 4.02525 9.861 4.02525 9.56775 4.31775C9.27525 4.611 9.27525 5.08575 9.56775 5.379ZM12 3.75V5.25C12 5.664 12.336 6 12.75 6C13.164 6 13.5 5.664 13.5 5.25V3.75C13.5 3.336 13.164 3 12.75 3C12.336 3 12 3.336 12 3.75Z"
                          fill="white"
                          stroke="white"
                          strokeWidth="0.25"
                        />
                      </svg>

                      <span className="ms-3">Coaches Corner</span>
                    </NavLink>
                  </>
                )}
              </div>
            </div>
            <div>
              <div className="space-y-2 font-medium">
                {userInfo.role === "super" && (
                  <NavLink
                    onClick={() => setToggleSidebar(!toggleSidebar)}
                    to="/media-content"
                    className={({ isActive, isPending }) =>
                      [
                        isPending ? "pending" : "",
                        isActive ? "rounded-lg bg-headerBlue" : "",
                      ].join("menu ")
                    }
                  >
                    <svg
                      version="1.1"
                      id="Uploaded to svgrepo.com"
                      xmlns="http://www.w3.org/2000/svg"
                      width="20px"
                      height="20"
                      viewBox="0 0 32 32"
                      fill="#ffffff"
                    >
                      <path
                        className="feather_een"
                        d="M27.257,4.097l-16.331,6.534C10.779,10.262,10.422,10,10,10H5c-0.552,0-1,0.448-1,1v0H3
	c-1.105,0-2,0.895-2,2v4c0,1.105,0.895,2,2,2l1,0c0,0.552,0.448,1,1,1v2c0,1.657,1.343,3,3,3h4c1.657,0,3-1.343,3-3v-1l12.257,4.903
	C28.571,26.428,30,25.461,30,24.046V5.954C30,4.539,28.571,3.572,27.257,4.097z M4,18H3c-0.552,0-1-0.448-1-1v-4
	c0-0.552,0.448-1,1-1h1V18z M10.925,19.371L12,19.8V22H8l0-2h2C10.421,20,10.777,19.739,10.925,19.371z M5,11h5v8l-5,0V11z M12,24H8
	c-1.105,0-2-0.895-2-2v-2h1l0,2c0,0.552,0.448,1,1,1h4c0.552,0,1-0.448,1-1v-1.8l1,0.4V22C14,23.105,13.104,24,12,24z
	 M27.629,24.974L11,18.323v-6.646l16.629-6.651C28.285,4.763,29,5.247,29,5.954v18.092C29,24.753,28.285,25.237,27.629,24.974z"
                      />
                    </svg>
                    <span className="ms-3">Uploads</span>
                  </NavLink>
                )}
                {userInfo.role === "super" && (
                  <NavLink
                    onClick={() => setToggleSidebar(!toggleSidebar)}
                    to="/promo-codes"
                    className={({ isActive, isPending }) =>
                      [
                        isPending ? "pending" : "",
                        isActive ? "rounded-lg bg-headerBlue" : "",
                      ].join("menu ")
                    }
                  >
                    <svg
                      fill="#ffffff"
                      height="20px"
                      width="20px"
                      version="1.1"
                      id="Layer_1"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 512 512"
                      stroke="#ffffff"
                      strokeWidth="20.48"
                    >
                      <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                      <g
                        id="SVGRepo_tracerCarrier"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke="#CCCCCC"
                        strokeWidth="15.36"
                      >
                        {" "}
                        <g>
                          {" "}
                          <g>
                            {" "}
                            <path d="M495.432,111.165H384.267c-4.427,0-8.017,3.589-8.017,8.017v8.551c0,5.01-4.076,9.086-9.086,9.086 c-5.01,0-9.086-4.076-9.086-9.086v-8.551c0-4.427-3.589-8.017-8.017-8.017H16.568C7.432,111.165,0,118.597,0,127.733v256.534 c0,9.136,7.432,16.568,16.568,16.568h333.495c4.427,0,8.017-3.589,8.017-8.017v-8.551c0-5.01,4.076-9.086,9.086-9.086 c5.01,0,9.086,4.076,9.086,9.086v8.551c0,4.427,3.589,8.017,8.017,8.017h111.165c9.136,0,16.568-7.432,16.568-16.568V127.733 C512,118.597,504.568,111.165,495.432,111.165z M495.967,384.267c0,0.295-0.239,0.534-0.534,0.534H392.284v-0.534 c0-11.048-7.172-20.446-17.102-23.8v-10.405c0-4.427-3.589-8.017-8.017-8.017c-4.427,0-8.017,3.589-8.017,8.017v10.405 c-9.93,3.354-17.102,12.752-17.102,23.8v0.534H16.568c-0.295,0-0.534-0.239-0.534-0.534V127.733c0-0.295,0.239-0.534,0.534-0.534 h325.478v0.534c0,11.048,7.172,20.446,17.102,23.8v10.405c0,4.427,3.589,8.017,8.017,8.017c4.427,0,8.017-3.589,8.017-8.017 v-10.405c9.93-3.354,17.102-12.752,17.102-23.8v-0.534h103.148c0.295,0,0.534,0.239,0.534,0.534V384.267z"></path>{" "}
                          </g>{" "}
                        </g>{" "}
                        <g>
                          {" "}
                          <g>
                            {" "}
                            <path d="M367.165,188.125c-4.427,0-8.017,3.589-8.017,8.017v17.102c0,4.427,3.589,8.017,8.017,8.017 c4.427,0,8.017-3.589,8.017-8.017v-17.102C375.182,191.715,371.592,188.125,367.165,188.125z"></path>{" "}
                          </g>{" "}
                        </g>{" "}
                        <g>
                          {" "}
                          <g>
                            {" "}
                            <path d="M367.165,239.432c-4.427,0-8.017,3.589-8.017,8.017v17.102c0,4.427,3.589,8.017,8.017,8.017 c4.427,0,8.017-3.589,8.017-8.017v-17.102C375.182,243.021,371.592,239.432,367.165,239.432z"></path>{" "}
                          </g>{" "}
                        </g>{" "}
                        <g>
                          {" "}
                          <g>
                            {" "}
                            <path d="M367.165,290.739c-4.427,0-8.017,3.589-8.017,8.017v17.102c0,4.427,3.589,8.017,8.017,8.017 c4.427,0,8.017-3.589,8.017-8.017v-17.102C375.182,294.328,371.592,290.739,367.165,290.739z"></path>{" "}
                          </g>{" "}
                        </g>{" "}
                        <g>
                          {" "}
                          <g>
                            {" "}
                            <path d="M261.668,173.371c-3.131-3.131-8.207-3.131-11.337,0L96.411,327.292c-3.131,3.131-3.131,8.207,0,11.337 c1.565,1.566,3.617,2.348,5.668,2.348s4.103-0.782,5.668-2.348l153.921-153.921C264.799,181.578,264.799,176.501,261.668,173.371z "></path>{" "}
                          </g>{" "}
                        </g>{" "}
                        <g>
                          {" "}
                          <g>
                            {" "}
                            <path d="M127.733,162.472c-23.281,0-42.221,18.941-42.221,42.221s18.941,42.221,42.221,42.221s42.221-18.941,42.221-42.221 S151.013,162.472,127.733,162.472z M127.733,230.881c-14.44,0-26.188-11.748-26.188-26.188s11.748-26.188,26.188-26.188 s26.188,11.748,26.188,26.188S142.172,230.881,127.733,230.881z"></path>{" "}
                          </g>{" "}
                        </g>{" "}
                        <g>
                          {" "}
                          <g>
                            {" "}
                            <path d="M230.347,265.086c-23.281,0-42.221,18.941-42.221,42.221s18.941,42.221,42.221,42.221s42.221-18.941,42.221-42.221 S253.627,265.086,230.347,265.086z M230.347,333.495c-14.44,0-26.188-11.748-26.188-26.188s11.748-26.188,26.188-26.188 s26.188,11.748,26.188,26.188S244.786,333.495,230.347,333.495z"></path>{" "}
                          </g>{" "}
                        </g>{" "}
                      </g>
                      <g id="SVGRepo_iconCarrier">
                        {" "}
                        <g>
                          {" "}
                          <g>
                            {" "}
                            <path d="M495.432,111.165H384.267c-4.427,0-8.017,3.589-8.017,8.017v8.551c0,5.01-4.076,9.086-9.086,9.086 c-5.01,0-9.086-4.076-9.086-9.086v-8.551c0-4.427-3.589-8.017-8.017-8.017H16.568C7.432,111.165,0,118.597,0,127.733v256.534 c0,9.136,7.432,16.568,16.568,16.568h333.495c4.427,0,8.017-3.589,8.017-8.017v-8.551c0-5.01,4.076-9.086,9.086-9.086 c5.01,0,9.086,4.076,9.086,9.086v8.551c0,4.427,3.589,8.017,8.017,8.017h111.165c9.136,0,16.568-7.432,16.568-16.568V127.733 C512,118.597,504.568,111.165,495.432,111.165z M495.967,384.267c0,0.295-0.239,0.534-0.534,0.534H392.284v-0.534 c0-11.048-7.172-20.446-17.102-23.8v-10.405c0-4.427-3.589-8.017-8.017-8.017c-4.427,0-8.017,3.589-8.017,8.017v10.405 c-9.93,3.354-17.102,12.752-17.102,23.8v0.534H16.568c-0.295,0-0.534-0.239-0.534-0.534V127.733c0-0.295,0.239-0.534,0.534-0.534 h325.478v0.534c0,11.048,7.172,20.446,17.102,23.8v10.405c0,4.427,3.589,8.017,8.017,8.017c4.427,0,8.017-3.589,8.017-8.017 v-10.405c9.93-3.354,17.102-12.752,17.102-23.8v-0.534h103.148c0.295,0,0.534,0.239,0.534,0.534V384.267z"></path>{" "}
                          </g>{" "}
                        </g>{" "}
                        <g>
                          {" "}
                          <g>
                            {" "}
                            <path d="M367.165,188.125c-4.427,0-8.017,3.589-8.017,8.017v17.102c0,4.427,3.589,8.017,8.017,8.017 c4.427,0,8.017-3.589,8.017-8.017v-17.102C375.182,191.715,371.592,188.125,367.165,188.125z"></path>{" "}
                          </g>{" "}
                        </g>{" "}
                        <g>
                          {" "}
                          <g>
                            {" "}
                            <path d="M367.165,239.432c-4.427,0-8.017,3.589-8.017,8.017v17.102c0,4.427,3.589,8.017,8.017,8.017 c4.427,0,8.017-3.589,8.017-8.017v-17.102C375.182,243.021,371.592,239.432,367.165,239.432z"></path>{" "}
                          </g>{" "}
                        </g>{" "}
                        <g>
                          {" "}
                          <g>
                            {" "}
                            <path d="M367.165,290.739c-4.427,0-8.017,3.589-8.017,8.017v17.102c0,4.427,3.589,8.017,8.017,8.017 c4.427,0,8.017-3.589,8.017-8.017v-17.102C375.182,294.328,371.592,290.739,367.165,290.739z"></path>{" "}
                          </g>{" "}
                        </g>{" "}
                        <g>
                          {" "}
                          <g>
                            {" "}
                            <path d="M261.668,173.371c-3.131-3.131-8.207-3.131-11.337,0L96.411,327.292c-3.131,3.131-3.131,8.207,0,11.337 c1.565,1.566,3.617,2.348,5.668,2.348s4.103-0.782,5.668-2.348l153.921-153.921C264.799,181.578,264.799,176.501,261.668,173.371z "></path>{" "}
                          </g>{" "}
                        </g>{" "}
                        <g>
                          {" "}
                          <g>
                            {" "}
                            <path d="M127.733,162.472c-23.281,0-42.221,18.941-42.221,42.221s18.941,42.221,42.221,42.221s42.221-18.941,42.221-42.221 S151.013,162.472,127.733,162.472z M127.733,230.881c-14.44,0-26.188-11.748-26.188-26.188s11.748-26.188,26.188-26.188 s26.188,11.748,26.188,26.188S142.172,230.881,127.733,230.881z"></path>{" "}
                          </g>{" "}
                        </g>{" "}
                        <g>
                          {" "}
                          <g>
                            {" "}
                            <path d="M230.347,265.086c-23.281,0-42.221,18.941-42.221,42.221s18.941,42.221,42.221,42.221s42.221-18.941,42.221-42.221 S253.627,265.086,230.347,265.086z M230.347,333.495c-14.44,0-26.188-11.748-26.188-26.188s11.748-26.188,26.188-26.188 s26.188,11.748,26.188,26.188S244.786,333.495,230.347,333.495z"></path>{" "}
                          </g>{" "}
                        </g>{" "}
                      </g>
                    </svg>

                    <span className="ms-3">Promo Codes</span>
                  </NavLink>
                )}
                <NavLink
                  onClick={() => setToggleSidebar(!toggleSidebar)}
                  to="/settings"
                  className={({ isActive, isPending }) =>
                    [
                      isPending ? "pending" : "",
                      isActive ? "rounded-lg bg-headerBlue" : "",
                    ].join("menu ")
                  }
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M16.1801 13.3348L16.38 13.5449C17.16 14.3249 17.17 15.5949 16.38 16.3749C15.6 17.1549 14.33 17.1549 13.55 16.3749L13.34 16.1649C12.92 16.3949 12.47 16.5748 12.01 16.7148V17.0049C12.01 18.1049 11.12 19.0049 10.01 19.0049C8.91001 19.0049 8.01001 18.1149 8.01001 17.0049V16.7148C7.55001 16.5748 7.10005 16.3949 6.68005 16.1649L6.46997 16.3749C5.68997 17.1549 4.42001 17.1549 3.64001 16.3749C2.86001 15.5949 2.86001 14.3249 3.64001 13.5449L3.83997 13.3449C3.60997 12.9149 3.43004 12.4649 3.29004 12.0049H3C1.9 12.0049 1 11.1149 1 10.0049C1 8.89488 1.89 8.00488 3 8.00488H3.29004C3.43004 7.54488 3.60997 7.09493 3.83997 6.67493L3.63 6.46484C2.85 5.68484 2.85 4.41489 3.63 3.63489C4.41 2.85489 5.67996 2.85489 6.45996 3.63489L6.66003 3.83484H6.67004C7.09004 3.60484 7.54 3.42491 8 3.28491V2.99487C8 1.89487 8.89 0.994873 10 0.994873C11.1 0.994873 12 1.88487 12 2.99487V3.28491C12.46 3.42491 12.9101 3.60484 13.3301 3.83484H13.34L13.54 3.63489C14.32 2.85489 15.59 2.84489 16.37 3.63489C17.15 4.41489 17.15 5.68484 16.37 6.46484L16.16 6.67493C16.39 7.09493 16.57 7.54488 16.71 8.00488H17C18.1 8.00488 19 8.89488 19 10.0049C19 11.1149 18.11 12.0049 17 12.0049H16.71C16.57 12.4649 16.39 12.9148 16.16 13.3348"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M10.0164 13.0049C11.6732 13.0049 13.0164 11.6617 13.0164 10.0049C13.0164 8.34803 11.6732 7.00488 10.0164 7.00488C8.35952 7.00488 7.01637 8.34803 7.01637 10.0049C7.01637 11.6617 8.35952 13.0049 10.0164 13.0049Z"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="ms-3">Settings</span>
                </NavLink>
                {userInfo.role !== "super" && (
                  <NavLink
                    onClick={() => setToggleSidebar(!toggleSidebar)}
                    to="/help-center"
                    className={({ isActive, isPending }) =>
                      [
                        isPending ? "pending" : "",
                        isActive ? "rounded-lg bg-headerBlue" : "",
                      ].join("menu ")
                    }
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="19"
                      height="20"
                      viewBox="0 0 19 20"
                      fill="none"
                    >
                      <g clipPath="url(#clip0_983_9036)">
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M9.5 2.04446C13.8939 2.04446 17.4555 5.6064 17.4555 10C17.4555 14.3936 13.8936 17.9555 9.5 17.9555C5.1064 17.9555 1.54446 14.3939 1.54446 10C1.54446 5.60614 5.1064 2.04446 9.5 2.04446ZM9.5 0.5C14.7468 0.5 19 4.75322 19 10C19 15.2468 14.7468 19.5 9.5 19.5C4.25322 19.5 0 15.2468 0 10C0 4.75322 4.25322 0.5 9.5 0.5Z"
                          fill="white"
                        />
                        <path
                          d="M9.42361 14.8313C9.16434 14.8313 8.93771 14.7463 8.7437 14.5786C8.5497 14.4109 8.45383 14.1755 8.45383 13.8727C8.45383 13.6048 8.54767 13.3802 8.73485 13.1971C8.92228 13.0139 9.15094 12.9224 9.42336 12.9224C9.69578 12.9224 9.91811 13.0139 10.1012 13.1971C10.2844 13.38 10.3759 13.6046 10.3759 13.8727C10.3759 14.1714 10.2801 14.4066 10.0883 14.5766C9.89661 14.7466 9.67655 14.8316 9.42361 14.8316M6.30939 7.83622C6.30939 7.43303 6.44016 7.02352 6.69942 6.60717C6.95869 6.19311 7.3381 5.84885 7.83488 5.57644C8.33394 5.30402 8.91368 5.16895 9.57816 5.16895C10.1951 5.16895 10.7397 5.28226 11.2127 5.51118C11.6834 5.73781 12.0474 6.04716 12.3046 6.43947C12.5619 6.82951 12.6904 7.25673 12.6904 7.7143C12.6904 8.07601 12.6162 8.39421 12.4703 8.66663C12.3241 8.93905 12.1498 9.17454 11.9472 9.37057C11.7466 9.56887 11.3849 9.90225 10.8621 10.371C10.7159 10.5015 10.6006 10.6171 10.5133 10.7172C10.426 10.8176 10.3608 10.909 10.3193 10.9919C10.2758 11.0749 10.2429 11.1576 10.2191 11.2403C10.1974 11.323 10.1602 11.4692 10.1144 11.6784C10.0337 12.1208 9.78102 12.3409 9.35405 12.3409C9.13374 12.3409 8.94656 12.269 8.79606 12.1251C8.64354 11.9792 8.56943 11.7657 8.56943 11.4801C8.56943 11.1227 8.62381 10.8133 8.73485 10.5518C8.84614 10.2903 8.99209 10.0616 9.17522 9.8633C9.3581 9.66499 9.60674 9.42976 9.91609 9.15734C10.1885 8.91982 10.3868 8.73897 10.5067 8.61908C10.6289 8.49716 10.7313 8.36183 10.814 8.21386C10.8967 8.06564 10.9382 7.90224 10.9382 7.72796C10.9382 7.38801 10.812 7.10041 10.5591 6.8672C10.3041 6.63399 9.97958 6.51637 9.57841 6.51637C9.10996 6.51637 8.76571 6.63399 8.54337 6.8715C8.32331 7.10673 8.13588 7.45554 7.98336 7.91539C7.83716 8.39497 7.56272 8.63451 7.15953 8.63451C6.91974 8.63451 6.71915 8.55179 6.55575 8.38182C6.39235 8.21412 6.30939 8.03301 6.30939 7.83698V7.83622Z"
                          fill="white"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_983_9036">
                          <rect
                            width="19"
                            height="19"
                            fill="white"
                            transform="translate(0 0.5)"
                          />
                        </clipPath>
                      </defs>
                    </svg>
                    <span className="ms-3">Help Center</span>
                  </NavLink>
                )}

                <div
                  className="cursor-pointer menu"
                  onClick={(ev: React.MouseEvent<HTMLDivElement>) =>
                    handleSubmit(ev)
                  }
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="17"
                    height="18"
                    viewBox="0 0 17 18"
                    fill="none"
                  >
                    <path
                      d="M1 4.3125V3.375C1 2.33906 1.83906 1.5 2.875 1.5H14.125C15.1609 1.5 16 2.33906 16 3.375V14.625C16 15.6609 15.1609 16.5 14.125 16.5H2.875C1.83906 16.5 1 15.6609 1 14.625V13.6875"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M11.3127 9H1.93768"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M4.74991 12.75L1.30696 9.66328C0.899146 9.29765 0.899146 8.70469 1.30696 8.33672L4.74991 5.25"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="ms-3">Log Out</span>
                </div>
              </div>
              <div className="flex items-center py-5 truncate ">
                {endUserInfo && (
                  <img
                    className="w-10 h-10 rounded-full"
                    src={
                      endUserInfo?.profile?.avatar
                        ? endUserInfo?.profile?.avatar
                        : userInfo.avatar
                        ? userInfo?.avatar
                        : "/assets/icons/avatar.png"
                    }
                    alt="user_photo"
                  />
                )}
                <div>
                  <p className="font-semibold text-white capitalize ms-3">
                    {endUserInfo && endUserInfo.role === "user"
                      ? `${endUserInfo?.profile.first_name} ${endUserInfo?.profile.last_name}`
                      : `${userInfo.full_name}`}
                  </p>
                  {/* <p className="font-normal text-white truncate ms-3"> */}
                  <p
    className="font-normal text-white truncate ms-3 max-w-xs whitespace-nowrap overflow-hidden"
    title={userInfo.email}
  >
                    {userInfo.email}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

export default PrivateHeader;
